import {
  Component,
  OnInit,
  ViewChild,
  Inject,
  Input,
  Optional,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { CommonUtilsService } from '../../common-utils.service';
import { HttpMethods } from '../../../enums/http-method.enum';
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
  PAGE_OPTIONS,
} from '../../../constants/lib-constants';
import { keys, orderBy } from 'lodash';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CellStyleParameters,
  DataViewColumnsCustomClassConfig,
  DataViewParameters,
} from '../../../models/data-view-parameters';
import { MatMultiSort, MatMultiSortTableDataSource } from 'ngx-mat-multi-sort';
import { firstValueFrom, forkJoin, merge, Observable, of, Subject } from 'rxjs';
import { delay, switchMap, takeUntil } from 'rxjs/operators';
import { Sort } from '@angular/material/sort';

@Component({
  selector: 'ra-data-view',
  templateUrl: './data-view.component.html',
  styleUrls: ['./data-view.component.scss'],
  standalone: false,
})
export class DataViewComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  @Input() inputParameters?: DataViewParameters;
  @Input() selectedRowIndexes?: number[] = [];
  @Input() displayedColumns?: string[];
  @Input() defaultPageSize?: number = DEFAULT_PAGE_SIZE;
  @Input() columnClassConfig?: DataViewColumnsCustomClassConfig;
  @Input() readOnly: boolean = false;
  @Output() rowSelected = new EventEmitter<{
    rowIdx: number[] | number;
    event: string;
  }>();

  fileData: { [key: string]: any }[] = [];
  fileStats: { [key: string]: any } = {};
  preColoredStats: { [key: string]: any } = {};
  reverseColorCoding = false;
  fileName: string = '';
  customer: string = '';

  loading = false;
  defaultPageIndex = DEFAULT_PAGE_INDEX;
  pageOptions = PAGE_OPTIONS;
  showFirstLastButtons = true;
  enableSorting = true;

  tableDataSource: MatMultiSortTableDataSource<any> | { data: any[] } = {
    data: [],
  };

  private destroy$ = new Subject<void>();

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatMultiSort) sort: MatMultiSort;

  get dataSource() {
    return this.tableDataSource?.data ?? [];
  }

  get fileStatKeys() {
    return keys(this.fileStats || {});
  }

  get hasFileStats(): boolean {
    return this.fileStats && keys(this.fileStats).length > 0;
  }

  get filteredDisplayedColumns(): string[] {
    if (!this.displayedColumns) return [];
    return this.readOnly
      ? this.displayedColumns.filter((col) => col !== 'select')
      : this.displayedColumns;
  }

  constructor(
    private commonUtils: CommonUtilsService,
    @Optional() private dialogRef: MatDialogRef<DataViewComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: DataViewParameters,
  ) {
    if (dialogData) {
      this.enableSorting = false;
    }
  }

  ngOnInit(): void {
    const parameters = this.dialogData || this.inputParameters;

    if (parameters) {
      this.customer = parameters.customerName ?? this.customer;
      this.fileName = parameters.fileName ?? this.fileName;
      this.preColoredStats =
        parameters.colorCodeParams?.stats ?? this.preColoredStats;
      this.reverseColorCoding =
        parameters.colorCodeParams?.reverseColorCode ?? this.reverseColorCoding;

      if (parameters.fileData) {
        this.processFileData(parameters.fileData);
        this.fileStats = parameters.fileStats ?? {};
        setTimeout(() => this.initTable());
      } else if (parameters.downloadURL && parameters.fileStatsURL) {
        this.getData(parameters);
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.fileData?.length > 0) {
      this.initTable();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.selectedRowIndexes &&
      !changes.selectedRowIndexes.firstChange &&
      changes.selectedRowIndexes.currentValue.length === 0
    ) {
      this.selectedRowIndexes = [];
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private processFileData(data: any[]) {
    if (data?.length > 0) {
      const columns = keys(data[0]);
      this.displayedColumns = this.displayedColumns ?? [...columns];
      this.fileData = data;
    }
  }

  private initTable(): void {
    if (!this.paginator) return;

    if (this.enableSorting && this.sort) {
      this.tableDataSource = new MatMultiSortTableDataSource(this.sort);
      merge(
        (this.paginator.page as unknown as Observable<PageEvent>) ||
          of({} as PageEvent),
        (this.sort.sortChange as unknown as Observable<Sort>) || of({} as Sort),
      )
        .pipe(
          takeUntil(this.destroy$),
          delay(0),
          switchMap(() => of(this.fileData)),
        )
        .subscribe((data) => this.setTableData(data));
    } else {
      this.tableDataSource = { data: [] };
      (this.paginator.page as unknown as Observable<PageEvent>)
        ?.pipe(delay(0), takeUntil(this.destroy$))
        .subscribe(() => this.setTableData(this.fileData));
    }

    this.paginator.length = this.fileData.length;
    this.paginator.pageIndex = this.defaultPageIndex;
    this.paginator.pageSize = this.defaultPageSize;
    this.showFirstLastButtons =
      this.paginator.length > this.paginator.pageSize * 2;
    setTimeout(() => this.paginator.page.emit());
  }

  private setTableData(data: any[]) {
    const pageSize = this.paginator?.pageSize || this.defaultPageSize;
    const pageIndex = this.paginator?.pageIndex || this.defaultPageIndex;
    const startIdx = pageSize * pageIndex;
    const endIdx = startIdx + pageSize;

    let sortedData = data;
    if (this.enableSorting && this.sort) {
      const actives = this.sort?.actives ?? [];
      const directions = this.sort?.directions ?? [];
      sortedData = this.sortData(data, actives, directions);
    }

    this.tableDataSource.data = sortedData.slice(startIdx, endIdx);
  }

  private sortData(data: any[], actives: string[], directions: any[]): any[] {
    return orderBy(
      data,
      actives.map(
        (headerId) => (row) => this.sortingDataAccessor(row, headerId),
      ),
      directions,
    );
  }

  private sortingDataAccessor(row: any, sortHeaderId: string): any {
    const val = row?.[sortHeaderId];

    if (typeof val === 'string') {
      // Remove $, %, commas, plus sign, and trim the result
      const clean = val
        .replace(/[$,%]/g, '')
        .replace(/,/g, '')
        .replace(/\+$/, '')
        .trim();

      const num = parseFloat(clean);
      if (!isNaN(num)) {
        return num;
      }
      return val.toLowerCase();
    }
    if (typeof val === 'number') {
      return val;
    }
    return val;
  }

  async getData(parameters: DataViewParameters) {
    try {
      this.loading = true;
      if (parameters.downloadURL && parameters.fileStatsURL) {
        const { data, stats } = await firstValueFrom(
          forkJoin({
            data: this.commonUtils.getHttpCall(
              HttpMethods.Get,
              parameters.downloadURL,
            ),
            stats: this.commonUtils.getHttpCall(
              HttpMethods.Get,
              parameters.fileStatsURL,
            ),
          }),
        );
        this.loading = false;
        if (data?.length > 0) {
          this.processFileData(data);
          this.fileStats = stats;
          setTimeout(() => this.initTable());
        }
      }
    } catch (_err) {
      this.loading = false;
    }
  }

  onSelect(idx, ev) {
    const selectedRowIdx =
      this.paginator.pageIndex * this.paginator.pageSize + idx;
    this.fileData[selectedRowIdx]['select'] =
      !this.fileData[selectedRowIdx]['select'];
    if (ev.checked) {
      this.selectedRowIndexes.push(selectedRowIdx);
    } else {
      const idx = this.selectedRowIndexes.indexOf(selectedRowIdx);
      this.selectedRowIndexes.splice(idx, 1);
    }
    this.rowSelected.emit({
      rowIdx: this.selectedRowIndexes,
      event: 'on-select',
    });
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  onUndo(idx) {
    const selectedRowIdx =
      this.paginator.pageIndex * this.paginator.pageSize + idx;
    this.rowSelected.emit({ rowIdx: selectedRowIdx, event: 'on-undo' });
  }

  isEmpty(element) {
    if (!element) return true;
    return Object.values(element).some(
      (value) => value === null || value === undefined || value === '',
    );
  }

  getColumnClass(column: string, element: any): string {
    if (!this.columnClassConfig || !this.columnClassConfig[column]) {
      return '';
    }

    const config = this.columnClassConfig[column];

    // If no condition is provided or the condition evaluates to true, apply the class
    if (!config.condition || config.condition(element)) {
      return config.className;
    }

    return '';
  }

  getBackgroundColorForCell(
    column: string,
    value: any,
  ): CellStyleParameters | {} {
    return this.commonUtils.getCellStyle(
      column,
      value,
      this.preColoredStats,
      this.reverseColorCoding,
    );
  }
}
